import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft, Save, RotateCcw, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const OWNER_EMAIL = "trainer@demo.de";

export default function AdminLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && (user.email || "").toLowerCase() !== OWNER_EMAIL) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/landing/content"); setContent(data); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen"); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/landing/content", { content });
      toast.success("Landingpage gespeichert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Inhalt auf Werkseinstellungen zurücksetzen? Alle Änderungen gehen verloren.")) return;
    try {
      const { data } = await api.post("/landing/reset");
      setContent(data.content);
      toast.success("Auf Default zurückgesetzt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset fehlgeschlagen");
    }
  };

  const update = useCallback((path, value) => {
    setContent((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = value;
      return next;
    });
  }, []);

  if (!content) {
    return <div className="px-6 py-10 text-zinc-500 text-sm">Lädt…</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-5xl mx-auto" data-testid="admin-landing-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}
            className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-brand-lighter">Verwaltung</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase">Landingpage CMS</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="bg-transparent border-zinc-700 hover:bg-zinc-900 text-zinc-300 rounded-md" data-testid="al-preview">
              <ExternalLink className="h-3.5 w-3.5 sm:mr-2" /><span className="hidden sm:inline">Vorschau</span>
            </Button>
          </a>
          <Button variant="outline" size="sm" onClick={reset}
            className="bg-transparent border-zinc-700 hover:bg-zinc-900 text-zinc-300 rounded-md" data-testid="al-reset">
            <RotateCcw className="h-3.5 w-3.5 sm:mr-2" /><span className="hidden sm:inline">Reset</span>
          </Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="al-save">
            <Save className="h-3.5 w-3.5 mr-2" />{saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["hero"]} className="space-y-2">
        <Section value="seo" title="SEO" data-testid="sec-seo">
          <TextField label="Meta Title" value={content.seo.meta_title} onChange={(v) => update(["seo", "meta_title"], v)} />
          <TextArea label="Meta Description" value={content.seo.meta_description} onChange={(v) => update(["seo", "meta_description"], v)} />
          <TextField label="Open Graph Image URL" value={content.seo.og_image} onChange={(v) => update(["seo", "og_image"], v)} />
          <TextField label="Favicon URL" value={content.seo.favicon} onChange={(v) => update(["seo", "favicon"], v)} />
        </Section>

        <Section value="hero" title="Hero">
          <TextField label="Kicker" value={content.hero.kicker} onChange={(v) => update(["hero", "kicker"], v)} />
          <TextArea label="Headline (Zeilenumbrüche erlaubt)" rows={3} value={content.hero.headline} onChange={(v) => update(["hero", "headline"], v)} />
          <TextArea label="Subtitle" value={content.hero.subtitle} onChange={(v) => update(["hero", "subtitle"], v)} />
          <Row>
            <TextField label="CTA primär (Text)" value={content.hero.cta_primary} onChange={(v) => update(["hero", "cta_primary"], v)} />
            <TextField label="CTA primär (Link)" value={content.hero.cta_primary_link} onChange={(v) => update(["hero", "cta_primary_link"], v)} />
          </Row>
          <Row>
            <TextField label="CTA sekundär (Text)" value={content.hero.cta_secondary} onChange={(v) => update(["hero", "cta_secondary"], v)} />
            <TextField label="CTA sekundär (Link)" value={content.hero.cta_secondary_link} onChange={(v) => update(["hero", "cta_secondary_link"], v)} />
          </Row>
          <TextField label="Hero-Bild URL (leer = automatisches Mock-Dashboard)" value={content.hero.image_url} onChange={(v) => update(["hero", "image_url"], v)} />
        </Section>

        <Section value="problems" title="Problem-Sektion">
          <TextField label="Titel" value={content.problems.title} onChange={(v) => update(["problems", "title"], v)} />
          <TextArea label="Untertitel" value={content.problems.subtitle} onChange={(v) => update(["problems", "subtitle"], v)} />
          <ArrayEditor
            label="Karten"
            items={content.problems.cards}
            onChange={(list) => update(["problems", "cards"], list)}
            newItem={() => ({ icon: "Target", title: "", description: "" })}
            render={(it, set) => (
              <>
                <Row>
                  <TextField label="Icon (lucide name)" value={it.icon} onChange={(v) => set("icon", v)} />
                  <TextField label="Titel" value={it.title} onChange={(v) => set("title", v)} />
                </Row>
                <TextArea label="Beschreibung" value={it.description} onChange={(v) => set("description", v)} />
              </>
            )}
          />
        </Section>

        <Section value="solution" title="Lösungs-Sektion">
          <TextField label="Kicker" value={content.solution.kicker} onChange={(v) => update(["solution", "kicker"], v)} />
          <TextField label="Titel" value={content.solution.title} onChange={(v) => update(["solution", "title"], v)} />
          <TextArea label="Beschreibung" value={content.solution.description} onChange={(v) => update(["solution", "description"], v)} />
          <StringArrayEditor label="Highlights" items={content.solution.highlights || []} onChange={(l) => update(["solution", "highlights"], l)} />
        </Section>

        <Section value="features" title="Hauptfunktionen">
          <TextField label="Kicker" value={content.features.kicker} onChange={(v) => update(["features", "kicker"], v)} />
          <TextField label="Titel" value={content.features.title} onChange={(v) => update(["features", "title"], v)} />
          <ArrayEditor
            label="Karten"
            items={content.features.cards}
            onChange={(l) => update(["features", "cards"], l)}
            newItem={() => ({ icon: "Star", title: "", description: "" })}
            render={(it, set) => (
              <>
                <Row>
                  <TextField label="Icon" value={it.icon} onChange={(v) => set("icon", v)} />
                  <TextField label="Titel" value={it.title} onChange={(v) => set("title", v)} />
                </Row>
                <TextArea label="Beschreibung" value={it.description} onChange={(v) => set("description", v)} />
              </>
            )}
          />
        </Section>

        <Section value="screenshots" title="Screenshots">
          <TextField label="Kicker" value={content.screenshots.kicker} onChange={(v) => update(["screenshots", "kicker"], v)} />
          <TextField label="Titel" value={content.screenshots.title} onChange={(v) => update(["screenshots", "title"], v)} />
          <ArrayEditor
            label="Bilder"
            items={content.screenshots.items}
            onChange={(l) => update(["screenshots", "items"], l)}
            newItem={() => ({ image_url: "", caption: "", device: "desktop" })}
            render={(it, set) => (
              <>
                <Row>
                  <TextField label="Bild-URL" value={it.image_url} onChange={(v) => set("image_url", v)} />
                  <TextField label="Gerät (desktop/tablet/mobile)" value={it.device} onChange={(v) => set("device", v)} />
                </Row>
                <TextArea label="Beschreibung" rows={2} value={it.caption} onChange={(v) => set("caption", v)} />
              </>
            )}
          />
        </Section>

        <Section value="social_proof" title="Social Proof">
          <TextField label="Titel" value={content.social_proof.title} onChange={(v) => update(["social_proof", "title"], v)} />
          <ArrayEditor
            label="Statistik-Karten"
            items={content.social_proof.stats || []}
            onChange={(l) => update(["social_proof", "stats"], l)}
            newItem={() => ({ label: "", value: "" })}
            render={(it, set) => (
              <Row>
                <TextField label="Label" value={it.label} onChange={(v) => set("label", v)} />
                <TextField label="Wert" value={it.value} onChange={(v) => set("value", v)} />
              </Row>
            )}
          />
          <ArrayEditor
            label="Testimonials"
            items={content.social_proof.testimonials || []}
            onChange={(l) => update(["social_proof", "testimonials"], l)}
            newItem={() => ({ quote: "", author: "", role: "", avatar_url: "" })}
            render={(it, set) => (
              <>
                <TextArea label="Zitat" value={it.quote} onChange={(v) => set("quote", v)} />
                <Row>
                  <TextField label="Autor" value={it.author} onChange={(v) => set("author", v)} />
                  <TextField label="Rolle" value={it.role} onChange={(v) => set("role", v)} />
                </Row>
                <TextField label="Avatar-URL" value={it.avatar_url} onChange={(v) => set("avatar_url", v)} />
              </>
            )}
          />
          <StringArrayEditor label="Vereinslogo-URLs" items={content.social_proof.club_logos || []} onChange={(l) => update(["social_proof", "club_logos"], l)} />
        </Section>

        <Section value="pricing" title="Pricing">
          <TextField label="Kicker" value={content.pricing.kicker} onChange={(v) => update(["pricing", "kicker"], v)} />
          <TextField label="Titel" value={content.pricing.title} onChange={(v) => update(["pricing", "title"], v)} />
          <TextField label="Untertitel" value={content.pricing.subtitle} onChange={(v) => update(["pricing", "subtitle"], v)} />
          <ArrayEditor
            label="Tarife"
            items={content.pricing.plans}
            onChange={(l) => update(["pricing", "plans"], l)}
            newItem={() => ({ name: "", price_monthly: "0 €", price_yearly: "0 €", tagline: "", features: [], cta: "", cta_link: "", featured: false })}
            render={(it, set) => (
              <>
                <Row>
                  <TextField label="Name" value={it.name} onChange={(v) => set("name", v)} />
                  <TextField label="Tagline" value={it.tagline} onChange={(v) => set("tagline", v)} />
                </Row>
                <Row>
                  <TextField label="Preis Monatlich" value={it.price_monthly} onChange={(v) => set("price_monthly", v)} />
                  <TextField label="Preis Jährlich" value={it.price_yearly} onChange={(v) => set("price_yearly", v)} />
                </Row>
                <Row>
                  <TextField label="CTA Text" value={it.cta} onChange={(v) => set("cta", v)} />
                  <TextField label="CTA Link" value={it.cta_link} onChange={(v) => set("cta_link", v)} />
                </Row>
                <StringArrayEditor label="Features" items={it.features || []} onChange={(l) => set("features", l)} />
                <label className="flex items-center gap-2 text-xs text-zinc-400">
                  <input type="checkbox" checked={!!it.featured} onChange={(e) => set("featured", e.target.checked)} />
                  Hervorheben (Beliebt-Badge)
                </label>
              </>
            )}
          />
        </Section>

        <Section value="faq" title="FAQ">
          <TextField label="Kicker" value={content.faq.kicker} onChange={(v) => update(["faq", "kicker"], v)} />
          <TextField label="Titel" value={content.faq.title} onChange={(v) => update(["faq", "title"], v)} />
          <ArrayEditor
            label="Fragen"
            items={content.faq.items}
            onChange={(l) => update(["faq", "items"], l)}
            newItem={() => ({ question: "", answer: "" })}
            render={(it, set) => (
              <>
                <TextField label="Frage" value={it.question} onChange={(v) => set("question", v)} />
                <TextArea label="Antwort" value={it.answer} onChange={(v) => set("answer", v)} />
              </>
            )}
          />
        </Section>

        <Section value="cta_final" title="Finaler CTA">
          <TextField label="Titel" value={content.cta_final.title} onChange={(v) => update(["cta_final", "title"], v)} />
          <TextArea label="Untertitel" value={content.cta_final.subtitle} onChange={(v) => update(["cta_final", "subtitle"], v)} />
          <Row>
            <TextField label="CTA primär" value={content.cta_final.cta_primary} onChange={(v) => update(["cta_final", "cta_primary"], v)} />
            <TextField label="Link" value={content.cta_final.cta_primary_link} onChange={(v) => update(["cta_final", "cta_primary_link"], v)} />
          </Row>
          <Row>
            <TextField label="CTA sekundär" value={content.cta_final.cta_secondary} onChange={(v) => update(["cta_final", "cta_secondary"], v)} />
            <TextField label="Link" value={content.cta_final.cta_secondary_link} onChange={(v) => update(["cta_final", "cta_secondary_link"], v)} />
          </Row>
        </Section>

        <Section value="footer" title="Footer / Kontakt">
          <TextArea label="Tagline" value={content.footer.tagline} onChange={(v) => update(["footer", "tagline"], v)} />
          <TextField label="Adresse" value={content.footer.address} onChange={(v) => update(["footer", "address"], v)} />
          <Row>
            <TextField label="E-Mail" value={content.footer.email} onChange={(v) => update(["footer", "email"], v)} />
            <TextField label="Telefon" value={content.footer.phone} onChange={(v) => update(["footer", "phone"], v)} />
          </Row>
          <Row>
            <TextField label="Twitter" value={content.footer.social?.twitter} onChange={(v) => update(["footer", "social", "twitter"], v)} />
            <TextField label="Instagram" value={content.footer.social?.instagram} onChange={(v) => update(["footer", "social", "instagram"], v)} />
          </Row>
          <Row>
            <TextField label="LinkedIn" value={content.footer.social?.linkedin} onChange={(v) => update(["footer", "social", "linkedin"], v)} />
            <TextField label="YouTube" value={content.footer.social?.youtube} onChange={(v) => update(["footer", "social", "youtube"], v)} />
          </Row>
          <ArrayEditor
            label="Footer-Links (Impressum, Datenschutz, AGB, …)"
            items={content.footer.links || []}
            onChange={(l) => update(["footer", "links"], l)}
            newItem={() => ({ label: "", url: "", content: "" })}
            render={(it, set) => (
              <>
                <Row>
                  <TextField label="Label" value={it.label} onChange={(v) => set("label", v)} />
                  <TextField label="URL (z. B. # wenn Inhalt im Modal)" value={it.url} onChange={(v) => set("url", v)} />
                </Row>
                <TextArea
                  label="Inhalt (Markdown — wenn ausgefüllt, öffnet ein Modal statt URL)"
                  rows={8}
                  value={it.content || ""}
                  onChange={(v) => set("content", v)}
                />
                <div className="text-[10px] text-zinc-500">
                  Markdown unterstützt: # H1 · ## H2 · ### H3 · **fett** · *kursiv* · [Text](URL) · - Liste · Leerzeile = neuer Absatz
                </div>
              </>
            )}
          />
        </Section>
      </Accordion>

      <div className="mt-8 sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving}
          className="bg-brand hover:bg-brand-light text-white rounded-md shadow-lg h-11 px-6"
          data-testid="al-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? "Speichern…" : "Änderungen speichern"}
        </Button>
      </div>
    </div>
  );
}

function Section({ value, title, children }) {
  return (
    <AccordionItem value={value} className="border border-zinc-800 bg-zinc-900/40 rounded-md">
      <AccordionTrigger className="px-4 hover:no-underline data-[state=open]:text-brand-lighter">
        <span className="text-sm font-semibold uppercase tracking-wider">{title}</span>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-5 space-y-3">{children}</AccordionContent>
    </AccordionItem>
  );
}

const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;

function TextField({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1 h-9 rounded-md text-sm" />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1 rounded-md text-sm font-sans" />
    </div>
  );
}

function ArrayEditor({ label, items, onChange, newItem, render }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
        <Button size="sm" variant="ghost" onClick={() => onChange([...(items || []), newItem()])}
          className="text-brand-lighter hover:bg-zinc-800 h-7 text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" /> Hinzufügen
        </Button>
      </div>
      {(items || []).map((it, idx) => {
        const set = (k, v) => {
          const list = [...items];
          list[idx] = { ...list[idx], [k]: v };
          onChange(list);
        };
        const remove = () => onChange(items.filter((_, i) => i !== idx));
        const move = (delta) => {
          const list = [...items];
          const target = idx + delta;
          if (target < 0 || target >= list.length) return;
          [list[idx], list[target]] = [list[target], list[idx]];
          onChange(list);
        };
        return (
          <div key={idx} className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">#{idx + 1}</span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => move(-1)} className="h-6 w-6 p-0 text-zinc-500 hover:text-brand-lighter">↑</Button>
                <Button size="sm" variant="ghost" onClick={() => move(1)} className="h-6 w-6 p-0 text-zinc-500 hover:text-brand-lighter">↓</Button>
                <Button size="sm" variant="ghost" onClick={remove} className="h-6 w-6 p-0 text-zinc-500 hover:text-rose-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            {render(it, set)}
          </div>
        );
      })}
    </div>
  );
}

function StringArrayEditor({ label, items, onChange }) {
  return (
    <ArrayEditor
      label={label}
      items={items.map((s) => ({ v: s }))}
      onChange={(list) => onChange(list.map((x) => x.v))}
      newItem={() => ({ v: "" })}
      render={(it, set) => <TextField label="" value={it.v} onChange={(v) => set("v", v)} />}
    />
  );
}
